import type {
  CustomerStatusInput,
  HealthLabel,
  IntegrationStatusInput,
  KnowledgeStatusInput,
  RuntimeHealthRow,
  TenantHealthInput,
} from "./types";
import { resolveTenantHealthLabel } from "./health";
import {
  deriveRuntimeHealthDomains,
  flattenRuntimeHealthDomains,
} from "./runtimeDomains";
import { LABEL } from "./terminology";

export {
  RUNTIME_HEALTH_DOMAIN_META,
  RUNTIME_HEALTH_DOMAIN_ORDER,
  LEGACY_RUNTIME_HEALTH_MAPPING,
  assertExclusiveDomainMembership,
  deriveRuntimeHealthDomains,
  flattenRuntimeHealthDomains,
} from "./runtimeDomains";
export type {
  RuntimeHealthComponent,
  RuntimeHealthDomain,
  RuntimeHealthDomainId,
} from "./runtimeDomains";

/**
 * Runtime health tiles — flattened view of operational domains.
 * Prefer deriveRuntimeHealthDomains() for domain-grouped UI.
 *
 * Legacy technology-first names (API, Database, …) are remapped:
 * API → Concierge Runtime (Tenant Runtime); Database → PostgreSQL (Infrastructure);
 * AI Provider → OpenAI (Integrations); Email → SMTP (Integrations).
 */
export function deriveRuntimeHealth(
  customer: CustomerStatusInput,
  tenantHealth: TenantHealthInput | null,
  integrations?: IntegrationStatusInput[] | null,
  knowledge?: KnowledgeStatusInput | null,
): RuntimeHealthRow[] {
  const domains = deriveRuntimeHealthDomains({
    customer,
    tenantHealth,
    integrations,
    knowledge,
  });
  return flattenRuntimeHealthDomains(domains);
}

/** Overview health column — same four labels as everywhere else (Phase 2). */
export function deriveOverviewHealth(
  customer: CustomerStatusInput,
  tenantHealth: TenantHealthInput | null,
): HealthLabel {
  return resolveTenantHealthLabel({
    opsHealth: tenantHealth?.health,
    registryHealth: customer.last_health_status,
    hasStack: tenantHealth?.has_stack,
    hasRegistryObservation: Boolean(customer.last_health_status),
  });
}

export function backupStatusLabel(
  lastBackup: string | null | undefined,
  hasHealthRow: boolean,
): string {
  if (lastBackup) return "Recorded on disk";
  if (hasHealthRow) return "No backup found";
  return LABEL.unknown;
}
